// src/render/PhaserGame.tsx — React wrapper that mounts the Phaser.Game
// into a DOM container. Adapted from the canonical phaserjs/template-react-ts
// pattern (D-13), with a React 19 StrictMode double-invoke ref-guard
// (RESEARCH.md A3).
//
// Touch-action:none is applied to the canvas after mount (MOVE-06). Safe-area
// insets are pre-wired here (MOVE-07's functional requirement) so there is
// never a moment where the canvas doesn't respect notches.
//
// Phase 6 Plan 04 (WARNING-4) — module-scope EventBus subscription caches
// the active GameScene reference in `activeGameScene` so React layers
// (BubbleLayer rAF loop, camera-bias.ts) can synchronously read sprite
// positions and apply camera bias via getGameSceneRef(). The subscription
// is established at module-load (NOT inside a useEffect) because (a) it
// is idempotent across React 19 StrictMode double-invoke and (b) the cache
// must be populated the moment Phaser finishes its create() lifecycle, even
// if no React component has subscribed yet. The component-level
// gameRef.current StrictMode guard already follows this pattern.

import { useEffect, useRef, type JSX } from 'react';
import type Phaser from 'phaser';

import { attachVisibilityPause } from '../ui/utils/visibility';

import { createGame } from './game';
import { EventBus } from './EventBus';
import type { GameScene } from './scenes/GameScene';

// Phase 6 Plan 04 (WARNING-4) — cached active scene reference. Updated by
// the module-scope EventBus subscriptions below.
let activeGameScene: GameScene | null = null;

/**
 * Phase 6 Plan 04 — synchronous accessor for the active GameScene. Used by
 * src/ui/bubbles/BubbleLayer.tsx (rAF loop reading
 * `getSpriteScreenPos(entityId)`) and src/ui/bubbles/camera-bias.ts
 * (calling `setCameraBias(dx, dy)`). Returns null between scene-shutdown
 * and the next current-scene-ready emit, so callers must guard.
 */
export function getGameSceneRef(): GameScene | null {
  return activeGameScene;
}

// Module-scope subscription. Idempotent across re-imports (Phaser
// EventEmitter dedupes by reference, but TypedEventBus does not — so the
// expectation is that this module is loaded ONCE per page load, which is
// the case under Vite/Rollup ESM).
EventBus.on('current-scene-ready', (scene) => {
  if (scene.scene.key === 'GameScene') {
    activeGameScene = scene as GameScene;
  }
});
EventBus.on('scene-shutdown', (scene) => {
  if (scene === (activeGameScene as unknown as Phaser.Scene)) {
    activeGameScene = null;
  }
});

export function PhaserGame(): JSX.Element {
  const parentRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);

  useEffect(() => {
    if (!parentRef.current) return;
    if (gameRef.current) return; // StrictMode double-invoke guard (RESEARCH.md A3)
    const game = createGame(parentRef.current);
    gameRef.current = game;
    // After mount, apply touch-action:none to the canvas (MOVE-06).
    const canvas = parentRef.current.querySelector('canvas');
    if (canvas) {
      canvas.style.touchAction = 'none';
    }
    // MOVE-08 — pause the Phaser ticker on backgrounding, resume on focus.
    const detachVisibility = attachVisibilityPause(game);
    return () => {
      detachVisibility();
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, []);

  return (
    <div
      ref={parentRef}
      data-testid="phaser-parent"
      style={{
        width: '100%',
        height: '100dvh', // UI-SPEC Touch Canvas Base — dynamic viewport handles iOS address bar
        backgroundColor: '#0d1f2d',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    />
  );
}
